<template>
  <div>
    <b-row>
      <b-col cols="12">
        <div class="d-flex justify-content-end my-1">
          <b-button v-if="HasPermission(PermissionList.Sales_Create)" variant="primary" to="/proforma/add" :disabled="loading">
            <span class="text-nowrap">Add Proforma</span>
          </b-button>
        </div>
      </b-col>
      <!-- search input -->
      <b-col  cols="12" md="2" sm="2" class="my-1">
        <b-form-group class="mb-0">
          <label class="d-inline-block text-sm-left mr-50">Per page</label>
          <b-form-select
            id="perPageSelect"
            v-model="perPage"
            size="sm"
            :options="pageOptions"
           
          />
        </b-form-group>
      </b-col>

      <b-col
        cols="12"
        class="d-flex justify-content-end mb-2"
        style="position: relative; z-index: 1"
      >
        <b-button v-if="HasPermission(PermissionList.Sales_Search)"
          variant="outline-danger"
          
          @click="clearFilters"
          :disabled="!anyFilterActive"
        >
          Clear Filters
        </b-button>
      </b-col>

      <b-col cols="12">
        <b-table
          striped
          hover
          responsive
          :per-page="perPage"
          :current-page="currentPage"
          :items="filteredSalesOrders"
          :fields="fields"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :sort-direction="sortDirection"
          @filtered="onFiltered"
        >
          <!-- Proforma Number Filter -->
          <template #head(orderNumber)>
            <div class="d-flex flex-column">
              <span>PROFORMA NUMBER</span>
              <b-form-input v-if="HasPermission(PermissionList.Sales_Search)"
                v-model="columnFilters.orderNumber"
                placeholder="Filter by Proforma Number"
                size="sm"
                @input="filterTable"
              />
            </div>
          </template>

          <!-- Customer Filter -->
          <template #head(leadClientName)>
            <div class="d-flex flex-column">
              <span>CUSTOMER</span>
              <b-form-input v-if="HasPermission(PermissionList.Sales_Search)"
                v-model="columnFilters.leadClientName"
                placeholder="Filter by Customer"
                size="sm"
                @input="filterTable"
              />
            </div>
          </template>

          <!-- Sales Person Filter -->
          <template #head(salesPersonId)>
            <div class="d-flex flex-column">
              <span>Sales Person</span>
              <b-form-input v-if="HasPermission(PermissionList.Sales_Search)"
                v-model="columnFilters.salesPersonId"
                placeholder="Filter by Sales Person"
                size="sm"
                @input="filterTable"
              />
            </div>
          </template>

          <template v-slot:cell(jobCardDetails.jobCardNumber)="data">
            <div
              v-if="
                data.item.jobCardDetails && data.item.jobCardDetails.length > 0
              "
            >
              <b-button
                variant="primary"
                @click="openModal(data.item.jobCardDetails)"
              >
                View
              </b-button>
            </div>
          </template>

          <template #cell(isActive)="data">
            <b-badge v-if="data.item.isActive" variant="primary">
              Active
            </b-badge>
            <b-badge v-else variant="danger"> Inactive </b-badge>
          </template>

          <template #cell(total)="data">
            {{
              data.item.currency == null
                ? "Rs"
                : getCurrencySymbol(data.item.currency)
            }}
            {{ data.item.total.toFixed(2) }}
          </template>

          <template #cell(salesPersonId)="data">
            <p v-if="data.item.salesPersonId === null">-</p>
            <p v-else>{{ getSalePersonName(data.item.salesPersonId) }}</p>
          </template>

          <template #head(issuedDate)>
            <div class="d-flex flex-column">
              <span>Issue Date</span>
              <date-picker
                v-model="columnFilters.issuedDateRange"
                :range="true"
                format="YYYY-MM-DD"
                placeholder="Select Date Range"
                @change="filterTable"
                :clearable="false"
                :input-attr="{ readonly: true }"
                class="small-datepicker"
              />
            </div>
          </template>

          <template #head(orderType)>
            <div class="d-flex flex-column">
              <span>ORDER TYPE</span>
              <b-form-select
                v-model="columnFilters.orderType"
                @change="filterTable"
                size="sm"
              >
                <option value="">All</option>
                <!-- <option value="Fixed Order">Fixed Order</option> -->
                <option value="Variable Order">Variable Order</option>
                <option value="OneOff Order">OneOff Order</option>
                <option value="Cancelled">Cancelled</option>
              </b-form-select>
            </div>
          </template>

          <template #head(createdOn)>
            <div class="d-flex flex-column">
              <span>Created On</span>
              <date-picker
                v-model="columnFilters.createdDateRange"
                :range="true"
                format="YYYY-MM-DD"
                placeholder="Select Date Range"
                @change="filterTable"
                :clearable="false"
                :input-attr="{ readonly: true }"
                class="small-datepicker"
              />
            </div>
          </template>

          <!-- Display formatted issuedDate and createdOn in the table body -->
          <template #cell(issuedDate)="data">
            {{ convertTimestampToDate(data.item.issuedDate) }}
          </template>

          <template #cell(createdOn)="data">
            {{ convertTimestampToDate(data.item.createdOn) }}
          </template>

          <template #head(status)>
            <div class="d-flex flex-column">
              <span>STATUS</span>
              <b-form-select
                v-model="columnFilters.status"
                @change="filterTable"
                size="sm"
              >
                <option value="">All</option>
                <option value="PROFORMA">PROFORMA</option>
                <option value="ONDEMAND">ONDEMAND</option>
                <option value="READY">READY</option>
                <option value="INVOICED">INVOICED</option>
                <option value="CANCELLED">CANCELLED</option>
                <option value="Blocked">Blocked</option>
              </b-form-select>
            </div>
          </template>

          <!-- Display orderType as Badge in Table -->
          <template #cell(orderType)="data">
            <!-- <b-badge v-if="data.item.orderType === 0" variant="info"
              >Fixed Order</b-badge
            > -->
            <b-badge v-if="data.item.orderType === 1" variant="primary"
              >Variable Order</b-badge
            >
            <b-badge v-else-if="data.item.orderType === 2" variant="secondary"
              >OneOff Order</b-badge
            >
            <b-badge v-else-if="data.item.orderType === 3" variant="warning"
              >Cancelled</b-badge
            >
          </template>

          <!-- Display status as Badge in Table -->
          <template #cell(status)="data">
            <b-badge v-if="data.item.status === 0" variant="secondary"
              >PROFORMA</b-badge
            >
            <b-badge v-else-if="data.item.status === 1" variant="warning"
              >ONDEMAND</b-badge
            >
            <b-badge v-else-if="data.item.status === 2" variant="info"
              >READY</b-badge
            >
            <b-badge v-else-if="data.item.status === 3" variant="info"
              >INVOICED</b-badge
            >
            <b-badge v-else-if="data.item.status === 4" variant="danger"
              >CANCELLED</b-badge
            >
            <b-badge v-else variant="danger">Blocked</b-badge>
          </template>

          <template #cell(actions)="data">
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none"
              no-caret
            >
              <template v-slot:button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="text-body align-middle mr-25"
                />
              </template>
              <b-dropdown-item :to="getSalesOrderIdView(data.item.id)">
                <feather-icon icon="EyeIcon" class="mr-50" />
                <span>View</span>
              </b-dropdown-item>
              <b-dropdown-item
                :to="getSalesOrderId(data.item.id)"
                v-if="data.item.status === 0"
              >
                <feather-icon icon="Edit2Icon" class="mr-50" />
                <span>Edit</span>
              </b-dropdown-item>
              <b-dropdown-item
                @click="removesalesOrder(data.item)"
                v-if="data.item.status === 0 || data.item.status === 1"
              >
                <feather-icon icon="TrashIcon" class="mr-50" />
                <span>Cancel</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
        <div class="d-flex justify-content-center mb-1">
          <b-alert
            variant="warning"
            show
            v-if="salesOrderList.length === 0"
            class="d-flex justify-content-center"
          >
            <h4 class="alert-heading">No Proformas yet</h4>
          </b-alert>
        </div>
        <div class="d-flex justify-content-center mb-1">
          <b-spinner
            variant="success"
            label="Spinning"
            block
            v-if="loading"
            style="width: 3rem; height: 3rem"
          ></b-spinner>
        </div>
      </b-col>

      <b-col cols="12">
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          align="center"
          size="sm"
          class="my-0"
        />
        <b-form-group>
          Showing 
        {{ totalRows > 0 ? (perPage * (currentPage - 1) + 1) : 0 }} 
        to 
        {{ totalRows > 0 ? Math.min(perPage * currentPage, totalRows) : 0 }} 
        of 
        {{ totalRows > 0 ? totalRows : 0 }} 
        entries
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Modal -->
    <b-modal
      id="jobCardDetailsModal"
      title="Job Card Details"
      size="xl"
      ref="jobCardModal"

      hide-footer
    >
      <b-table
        striped
        hover
        responsive
        :items="modalJobCards"
        :fields="modalFields"
      >
        <template #cell(name)="data">
          <b-tooltip title="View details" placement="top">
          <a @click.prevent="goToProductPage(data.item.id)" style="cursor: pointer; text-decoration: underline; color: blue;">
            {{ data.item.name }}
          </a>        
        </b-tooltip>
        </template>
        <!-- <template #cell(productName)="data">
          {{ data.item.salesOrderDetail.productName }}
        </template> -->
        <template #cell(startDate)="data">
          {{ dayjs(data.item.startDate).format("DD/MM/YYYY") }}
        </template>
        <template #cell(endDate)="data">
          {{ dayjs(data.item.endDate).format("DD/MM/YYYY") }}
        </template>
        <template v-slot:cell(status)="data">
          <b-badge v-if="data.item.status === 0" variant="info">
            <span>Not Started</span>
          </b-badge>
          <b-badge v-else-if="data.item.status === 1" variant="secondary">
            <span>Started</span>
          </b-badge>
          <b-badge v-else-if="data.item.status === 2" variant="primary">
            <span>Completed</span>
          </b-badge>
          <b-badge v-else-if="data.item.status === 3" variant="danger">
            <span>Cancelled</span>
          </b-badge>
          <b-badge v-else-if="data.item.status === 4" variant="warning">
            <span>Blocked</span>
          </b-badge>
        </template>
      </b-table>
    </b-modal>
  </div>
  <!-- End of root element -->
</template>

<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import {
  BTable,
  BAvatar,
  BAlert,
  BBadge,
  BRow,
  BCol,
  BFormGroup,
  BFormSelect,
  BPagination,
  BInputGroup,
  BFormInput,
  BInputGroupAppend,
  BButton,
  BSpinner,
  BDropdown,
  BDropdownItem,
  BLink,
} from "bootstrap-vue";

import { mapActions, mapGetters } from "vuex";
import dayjs from "dayjs";
import { CurrencySymbols } from "@/libs/defaultList";

import { PermissionList } from '@/libs/defaultList';
import { HasPermission } from '@/@core/utils/validations/permissionCheck';
export default {
  components: {
    BTable,
    BAvatar,
    BBadge,
    BRow,
    BCol,
    BFormGroup,
    BAlert,
    BFormSelect,
    BPagination,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BButton,
    BSpinner,
    BDropdown,
    BDropdownItem,
    BLink,
    DatePicker,
  },
  data() {
    return {
      dayjs,
      idLead: this.$route.params.id,
      leadClientName: "",
      total: "",
      issuedDate: "",
      orderType: "",
      isActive: 1,
      status: "",
      orderNumber: "",
      salesOrderData: [],
      perPage: 5,
      pageOptions: [3, 5, 10],
      totalRows: 0,
      currentPage: 0,
      sortBy: "",
      sortDesc: false,
      sortDirection: "asc",
      filter: null,
      columnFilters: {
        orderNumber: "",
        leadClientName: "",
        salesPersonId: "",
        orderType: "",
        status: "",
        issuedDateStart: null,
        issuedDateEnd: null,
        createdDateStart: null,
        createdDateEnd: null,
      },
      filterOn: [],
      infoModal: {
        id: "info-modal",
        title: "",
        content: "",
      },
      fields: [
        { key: "orderNumber", label: "PROFORMA NUMBER", sortable: true },
        { key: "jobCardDetails.jobCardNumber", label: "View Job Card" },
        { key: "leadClientName", label: "CUSTOMER", sortable: true },
        { key: "total", label: "TOTAL (RS)" },
        { key: "issuedDate", label: "ISSUE DATE", sortable: true },
        // { key: "orderType", label: "ORDER TYPE", sortable: true },

        { key: "salesPersonId", label: "Sales Person", sortable: true },
        { key: "createdOn", label: "Created On", sortable: true },
        { key: "status", label: "STATUS", sortable: true },
        "actions",
      ],
      modalJobCards: [],
      modalFields: [
        { key: "name", label: "Job Card Name" },
        // { key: "productName", label: "Product" },
        { key: "startDate", label: "Start Date" },
        { key: "endDate", label: "End Date" },
        { key: "status", label: "Status" },
        { key: "priority", label: "Priority" },
      ],
    };
  },
  computed: {
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter((f) => f.sortable)
        .map((f) => ({ text: f.label, value: f.key }));
    },
    ...mapGetters("salesPersonModule", {
      SalesPersonList: "salesperson",
      salespersonloading: "loading",
    }),

    ...mapGetters("salesOrderModule", {
      salesOrderList: "salesorders",
      loading: "loading",
    }),

    ...mapGetters("leadModule", {
      leadId: "leadId",
      //salesOrderList2: "salesorders",
    }),
    filteredSalesOrders() {
      return this.salesOrderList.filter((item) => {
        const matchesOrderNumber =
          !this.columnFilters.orderNumber ||
          String(item.orderNumber)
            .toLowerCase()
            .includes(this.columnFilters.orderNumber.toLowerCase());
        const matchesCustomer =
          !this.columnFilters.leadClientName ||
          String(item.leadClientName || "")
            .toLowerCase()
            .includes(this.columnFilters.leadClientName.toLowerCase());
        const matchesSalesPerson =
          !this.columnFilters.salesPersonId ||
          this.getSalePersonName(item.salesPersonId)
            .toLowerCase()
            .includes(this.columnFilters.salesPersonId.toLowerCase());
        // const matchesOrderType =
        //   !this.columnFilters.orderType ||
        //   this.getOrderTypeText(item.orderType) ===
        //     this.columnFilters.orderType;
        const matchesStatus =
          !this.columnFilters.status ||
          this.getStatusText(item.status) === this.columnFilters.status;

        // Date range filtering
        const issuedDate = new Date(item.issuedDate);
        const createdDate = new Date(item.createdOn);
        const matchesIssuedDateRange =
          !this.columnFilters.issuedDateRange ||
          (issuedDate >= new Date(this.columnFilters.issuedDateRange[0]) &&
            issuedDate <= new Date(this.columnFilters.issuedDateRange[1]));
        const matchesCreatedDateRange =
          !this.columnFilters.createdDateRange ||
          (createdDate >= new Date(this.columnFilters.createdDateRange[0]) &&
            createdDate <= new Date(this.columnFilters.createdDateRange[1]));

        return (
          matchesOrderNumber &&
          matchesCustomer &&
          matchesSalesPerson &&
          // matchesOrderType &&
          matchesStatus &&
          matchesIssuedDateRange &&
          matchesCreatedDateRange
        );
      });
    },
    anyFilterActive() {
      const filters = this.columnFilters;
      return (
        filters.orderNumber ||
        filters.leadClientName ||
        filters.salesPersonId ||
        filters.orderType ||
        filters.status ||
        (filters.issuedDateStart && filters.issuedDateEnd) ||
        (filters.createdDateStart && filters.createdDateEnd)
      );
    },
  },
  mounted() {
    // Set the initial number of items
    this.totalRows = this.salesOrderList.length;
  },
  methods: {
    ...mapActions("salesPersonModule", ["getServiceRequestsListAction"]),
    ...mapActions("jobcardModule", ["getJobcardIdAction"]),
    getSalePersonName(id) {
      const salesPerson = this.SalesPersonList.find((s) => s.id === id);
      return salesPerson
        ? `${salesPerson.firstName} ${salesPerson.lastName}`
        : "-";
    },
    convertTimestampToDate(timestamp) {
      return new Date(timestamp).toLocaleDateString();
    },
    getSalesOrderIdView(data) {
      return "/proforma/view/" + data;
    },
    getSalesOrderIdDownload(data) {
      return "/proforma/download/" + data;
    },
    getsalesOrderData(data) {
      this.salesOrderData = data;
    },
    getSalesOrderId(item) {
      return "/proforma/edit/" + item;
    },
    getCurrencySymbol(currency) {
      return CurrencySymbols[currency];
    },

    goToProductPage(id) {
      const url = `${window.location.origin}/jobcard/view/${id}`;
      window.open(url, '_blank');
    },

    async fetchJobCardDetails(jobCardId) {
      try {
        const jobCardDetail = await this.getJobcardIdAction(jobCardId); // Make sure this returns the data
        if (!jobCardDetail) {
          console.error("No job card details found for ID:", jobCardId);
          return null; // Or handle the error as needed
        }

        // Proceed with using jobCardDetail
        return jobCardDetail;
      } catch (error) {
        console.error("Error fetching job card details:", error);
        return null;
      }
    },

    // Update openModal to fetch and display job card details
    async openModal(jobCardDetails) {
      // Initialize an empty array to store job card data
      const jobCardData = [];



      // Fetch details for each job card ID
      for (const jobCard of jobCardDetails) {
        const detail = await this.fetchJobCardDetails(jobCard.jobCardId);
        console.log("?? ~ openModal ~ detail:", detail);
        if (detail) {

          if (!jobCardData.some((item) => item.name === detail.name)) {
  jobCardData.push(detail);
}
        }
      }

      console.log("jobCardData before modal display:", jobCardData);
      this.modalJobCards = [...new Set(jobCardData)];

      console.log("🚀 ~ openModal ~ this.modalJobCards:", this.modalJobCards)
      this.$refs.jobCardModal.show();
    },
    getStatusText(status) {
      const statuses = [
        "Not Started",
        "Started",
        "Completed",
        "Cancelled",
        "Blocked",
      ];
      return statuses[status] || "Unknown";
    },
    getStatusVariant(status) {
      const variants = ["info", "secondary", "primary", "danger", "warning"];
      return variants[status] || "secondary";
    },

    removesalesOrder(item) {
      let payload = {
        id: item.id,
        orderNumber: item.orderNumber,
        leadClientId: item.leadClientId,
        salesPersonId: item.salesPersonId,
        issuedDate: item.issuedDate,
        dueDate: item.dueDate,
        note: item.note,
        paymentMethod: item.paymentMethod,
        status: 4,
        orderType: item.orderType,
        subTotal: item.subTotal,
        discount: item.discount,
        tax: item.tax,
        total: item.total,
        orderItems: item.orderItems,
      };
      this.updateSalesOrdersAction(payload)
        .then(() => {
          this.$swal({
            icon: "success",
            title: "Proforma cancelled successfully",
            customClass: {
              confirmButton: "btn btn-success",
            },
          });
          this.getSalesOrdersListAction();
        })
        .catch((error) => {
          this.error(error);
        });
    },
    info(item, index, button) {
      this.infoModal.title = `Row index: ${index}`;
      this.infoModal.content = JSON.stringify(item, null, 2);
      this.$root.$emit("bv::show::modal", this.infoModal.id, button);
    },
    resetInfoModal() {
      this.infoModal.title = "";
      this.infoModal.content = "";
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    ...mapActions("salesOrderModule", [
      "getSalesOrdersListAction",
      "removeSalesOrdersAction",
      "updateSalesOrdersAction",
    ]),
    ...mapActions("leadModule", ["getLeadQuotationsAction"]),

    getId(data) {
      return "/jobcard/view/" + data;
    },
    filterTable() {
      this.currentPage = 1;
      this.totalRows = this.filteredSalesOrders.length;
    },
    clearFilters() {
      this.columnFilters = {
        orderNumber: "",
        leadClientName: "",
        salesPersonId: "",
        orderType: "",
        status: "",
        issuedDateRange: null,
        createdDateRange: null,
      };
      this.filterTable();
    },
    getOrderTypeText(orderType) {
      switch (orderType) {
        // case 0:
        //   return "Fixed Order";
        case 1:
          return "Variable Order";
        case 2:
          return "OneOff Order";
        case 3:
          return "Cancelled";
        default:
          return "Unknown";
      }
    },
    getStatusText(status) {
      switch (status) {
        case 0:
          return "PROFORMA";
        case 1:
          return "ONDEMAND";
        case 2:
          return "READY";
        case 3:
          return "INVOICED";
        case 4:
          return "CANCELLED";
        default:
          return "Blocked";
      }
    },
  },
  async mounted() {
    await this.getSalesOrdersListAction()
      .then(() => {
        this.totalRows = this.salesOrderList.length;
      })
      .catch((response) => {
        console.log(response);
      });
  },
  setup(props){
    return{
      PermissionList,
      HasPermission
    }
  }
};
</script>

<style scoped>
.small-datepicker {
  width: 150px;
  /* Adjust width as desired */
}
</style>
